import Axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import { api } from '../Api';
import ConfirmActionModal from './ConfirmActionModal';
import { useOtpGate, OtpGateModal } from './OtpGate';

const DEFAULT_PHOTO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

const tk = (n) => `Tk ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

const STATUS_CHIP = {
  pending: { background: '#fef3c7', color: '#92400e', label: 'Pending' },
  processing: { background: '#fef3c7', color: '#92400e', label: 'Processing' },
  approved: { background: '#dcfce7', color: '#166534', label: 'Approved' },
  rejected: { background: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  cancelled: { background: '#f1f5f9', color: '#475569', label: 'Cancelled' },
};

/**
 * REQUEST MONEY — the merchant's tab on the Withdraw Requests page.
 *
 * Members below this merchant who paid them offline ask for the same amount
 * of Shopping Balance. Approve moves it from the merchant to the member
 * (password, then an OTP when the "Request Money approve" gate is on);
 * Reject needs a reason the member will see. Both open the confirm modal
 * first, with the merchant's balance before and after.
 *
 * Props: token, balance (merchant's Shopping Balance), disabled (office
 * disabled this merchant), onChanged (refresh balance + tab counts).
 */
export default function MerchantMoneyRequestsPanel({ token, balance = 0, disabled = false, onChanged }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [list, setList] = useState({ rows: [], page: 1, pages: 1, count: 0, pendingCount: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: '', status: 'pending', from: '', to: '' });
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // { kind: 'approve' | 'reject', row }
  const [confirm, setConfirm] = useState(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const failed = useRef(false);

  const load = useCallback(
    async (page = 1, f = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize: 10 });
        if (f.q) params.set('q', f.q);
        if (f.status) params.set('status', f.status);
        if (f.from) params.set('from', f.from);
        if (f.to) params.set('to', f.to);
        const { data } = await Axios.get(api + `/api/money-requests/merchant?${params.toString()}`, auth);
        setList(data);
      } catch (error) {
        toast.error(getError(error));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(() => {
    load(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refresh = () => {
    load(list.page, filters);
    onChanged?.();
  };

  const apply = (overrides = {}) => {
    const next = { q: search.trim(), status: filters.status, from, to, ...overrides };
    setFilters(next);
    load(1, next);
  };

  const gate = useOtpGate({
    base: '/api/money-requests',
    path: 'merchant/approve',
    token,
    onSuccess: (data) => {
      toast.success(data?.message || 'Approved.');
      setConfirm(null);
      setPassword('');
      refresh();
    },
    onError: (err) => {
      failed.current = true;
      toast.error(getError(err));
    },
  });

  const doApprove = async (row) => {
    if (!password) {
      toast.error('Enter your password to approve.');
      return;
    }
    failed.current = false;
    await gate.start({ id: row._id, password });
    // Done, or the OTP modal has taken over. On an error the review stays
    // open so the merchant can fix the password and try again.
    if (!failed.current) setConfirm(null);
  };

  const doReject = async (row) => {
    if (reason.trim().length < 3) {
      toast.error('Write a short reason so the member knows why.');
      return;
    }
    try {
      const { data } = await Axios.post(api + '/api/money-requests/merchant/reject', { id: row._id, reason: reason.trim() }, auth);
      toast.success(data.message || 'Rejected.');
      setConfirm(null);
      setReason('');
      refresh();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const rows = list.rows || [];
  const filtersActive = filters.q || filters.from || filters.to;

  return (
    <section className="tu-panel">
      <OtpGateModal gate={gate} title="Confirm this approval" actionLabel="Approve" />

      <h6 className="tu-panel-head">
        <i className="fa fa-hand-o-up" aria-hidden="true"></i> Request Money
        <span className="tu-count">{list.count}</span>
      </h6>
      <p className="tu-muted" style={{ margin: 0 }}>
        Members below you who paid you outside the app ask for the same amount of Shopping Balance. Check
        the money arrived, then approve: the amount leaves your Shopping Balance and lands in theirs.
      </p>

      <div className="tu-tabs">
        {STATUS_TABS.map((t) => (
          <button
            type="button"
            key={t.value || 'all'}
            className={filters.status === t.value ? 'tu-tab tu-tab-green tu-tab-active' : 'tu-tab tu-tab-green'}
            onClick={() => apply({ status: t.value })}
          >
            {t.label}
            {t.value === 'pending' ? <span className="tu-count">{list.pendingCount || 0}</span> : null}
          </button>
        ))}
      </div>

      <div className="tu-filters">
        <input
          className="tu-input"
          placeholder="Search member or Trx ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
        />
        <input className="tu-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From date" />
        <input className="tu-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To date" />
        <button type="button" className="tu-btn tu-btn-primary" onClick={() => apply()}>
          Search
        </button>
        {filtersActive ? (
          <button
            type="button"
            className="tu-btn tu-btn-light"
            onClick={() => {
              setSearch('');
              setFrom('');
              setTo('');
              apply({ q: '', from: '', to: '' });
            }}
          >
            Clear
          </button>
        ) : null}
      </div>

      {loading && rows.length === 0 ? (
        <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>
          <div className="tu-empty-icon">
            <i className="fa fa-inbox" aria-hidden="true"></i>
          </div>
          {filters.status === 'pending' ? 'No money requests waiting on you.' : 'No money requests here yet.'}
        </div>
      ) : (
        <div className="tu-wd-list">
          {rows.map((r) => {
            const chip = STATUS_CHIP[r.status] || STATUS_CHIP.pending;
            return (
              <article key={r._id} className="tu-wd-card">
                <img src={r.requesterPhoto || DEFAULT_PHOTO} alt="" className="tu-wd-avatar" />
                <div className="tu-wd-info">
                  <div className="tu-wd-name">{r.requesterName || r.requesterUsername}</div>
                  <div className="tu-wd-username">
                    @{r.requesterUsername} · {r.serialText}
                  </div>
                  <div className="tu-wd-date">
                    <i className="fa fa-clock-o" aria-hidden="true"></i> {new Date(r.createdAt).toLocaleString()}
                  </div>
                  {r.trxId ? <div className="tu-wd-date">Trx ID: {r.trxId}</div> : null}
                  {r.note ? <div className="tu-wd-date">Note: {r.note}</div> : null}
                  {r.status === 'rejected' && r.reason ? (
                    <div className="tu-wd-date" style={{ color: '#991b1b' }}>Reason: {r.reason}</div>
                  ) : null}
                </div>
                <div className="tu-wd-amounts">
                  <div className="tu-wd-amount-tk">{tk(r.amountBdt)}</div>
                </div>
                <div className="tu-wd-side">
                  <span className="tu-chip" style={{ background: chip.background, color: chip.color }}>
                    {chip.label}
                  </span>
                  <div className="tu-wd-actions">
                    {r.status === 'approved' ? (
                      <a
                        className="tu-linkbtn"
                        target="_blank"
                        rel="noreferrer"
                        href={`${api}/api/money-requests/${r._id}/receipt.pdf?token=${token}&download=1`}
                      >
                        PDF
                      </a>
                    ) : null}
                    {r.status === 'pending' && !disabled ? (
                      <>
                        <button
                          type="button"
                          className="tu-linkbtn"
                          onClick={() => {
                            setPassword('');
                            setConfirm({ kind: 'approve', row: r });
                          }}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="tu-linkbtn"
                          style={{ color: '#b91c1c' }}
                          onClick={() => {
                            setReason('');
                            setConfirm({ kind: 'reject', row: r });
                          }}
                        >
                          Reject
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {list.pages > 1 ? (
        <div className="tu-pager">
          <button className="tu-btn tu-btn-light" disabled={list.page <= 1} onClick={() => load(list.page - 1, filters)}>
            ‹ Prev
          </button>
          <span className="tu-muted">
            Page {list.page} of {list.pages}
          </span>
          <button
            className="tu-btn tu-btn-light"
            disabled={list.page >= list.pages}
            onClick={() => load(list.page + 1, filters)}
          >
            Next ›
          </button>
        </div>
      ) : null}

      {confirm?.kind === 'approve' ? (() => {
        const r = confirm.row;
        const after = Number(balance) - Number(r.amountBdt || 0);
        return (
          <ConfirmActionModal
            show
            title="Approve money request"
            recipient={{ name: r.requesterName, username: r.requesterUsername, photo: r.requesterPhoto }}
            amount={tk(r.amountBdt)}
            amountHint={`${r.serialText} — from your Shopping Balance`}
            rows={[
              { label: 'Member', value: `${r.requesterName || r.requesterUsername} (@${r.requesterUsername})` },
              { label: 'Amount', value: tk(r.amountBdt) },
              ...(r.trxId ? [{ label: 'Transaction ID', value: r.trxId }] : []),
              ...(r.note ? [{ label: 'Note', value: r.note }] : []),
              { label: 'Your Shopping Balance now', value: tk(balance) },
              { label: 'Your Shopping Balance after', value: tk(after), strong: true, tone: after < 0 ? 'bad' : undefined },
            ]}
            note={
              `Only approve once @${r.requesterUsername} has paid you ${tk(r.amountBdt)}. ${tk(r.amountBdt)} leaves your ` +
              `Shopping Balance and is added to theirs, and both of you get the receipt. If a verification code is ` +
              'required, it is sent after you confirm.'
            }
            confirmLabel="Approve"
            busyLabel="Approving…"
            busy={gate.busy}
            onCancel={() => setConfirm(null)}
            onConfirm={() => doApprove(r)}
          >
            <input
              className="tu-input"
              type="password"
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </ConfirmActionModal>
        );
      })() : null}

      {confirm?.kind === 'reject' ? (() => {
        const r = confirm.row;
        return (
          <ConfirmActionModal
            show
            danger
            title="Reject money request"
            recipient={{ name: r.requesterName, username: r.requesterUsername, photo: r.requesterPhoto }}
            amount={tk(r.amountBdt)}
            amountHint={r.serialText}
            rows={[
              { label: 'Member', value: `${r.requesterName || r.requesterUsername} (@${r.requesterUsername})` },
              { label: 'Amount', value: tk(r.amountBdt) },
              { label: 'Your Shopping Balance now', value: tk(balance) },
              { label: 'Your Shopping Balance after', value: tk(balance), strong: true },
            ]}
            note="Nothing moves. The member is told it was rejected, with your reason."
            confirmLabel="Reject"
            busyLabel="Rejecting…"
            onCancel={() => setConfirm(null)}
            onConfirm={() => doReject(r)}
          >
            <textarea
              className="tu-input"
              rows={2}
              maxLength={300}
              placeholder="Reason (required), e.g. Payment not received"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </ConfirmActionModal>
        );
      })() : null}
    </section>
  );
}
